import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, TextField,
  MenuItem, RadioGroup, FormControlLabel, Radio, Checkbox, Chip, CircularProgress, Avatar,
  Divider, Stack, FormHelperText, alpha, useTheme, useMediaQuery,
} from "@mui/material";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import PremiumTimePicker from "../../../Components/PremiumTimePicker";
import GradeSubjectGroups from "./GradeSubjectGroups";
import { gsCount, rankNote } from "./makeupRank";
import {
  useRemoveStudentFromOccurrenceMutation, useGetMakeupOptionsMutation, useScheduleMakeupMutation,
  useGetScheduleDetailsQuery, useGetallLocationQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useConfirm } from "../../../Components/Confirm";

/**
 * Reasons the user can pick. "Other" forces a manual text entry that becomes the
 * REAL stored reason (we never persist the bare word "Other").
 */
const REASONS = ["Sick", "Family emergency", "School activity", "Transportation issue", "Vacation", "Medical appointment", "Personal appointment", "Staff-approved absence", "Other"];
const REASON_MAX = 500;

// Steps of the wizard.
const S_STUDENT = 0; // pick which assigned student (skipped when the occurrence has exactly one, or the page is filtered to one assigned student)
const S_ABSENCE = 1; // reason / custom reason / notes / makeup-required
const S_PREFS   = 2; // date / time / mode / location / subjects / staff preference
const S_AVAIL   = 3; // real availability options
const S_REVIEW  = 4; // review + confirm

const fmtTime = (t?: string) => {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  const d = new Date(); d.setHours(h, m || 0);
  return new Intl.DateTimeFormat("en-CA", { hour: "numeric", minute: "numeric", hour12: true }).format(d);
};
const initials = (n: string) => (n || "").split(" ").filter(Boolean).slice(0, 2).map((x) => x[0]?.toUpperCase()).join("") || "?";
const hhmm = (t?: string) => String(t || "").slice(0, 5);
const toMin = (t: string) => { const [h, m] = String(t || "").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
const minToHHmm = (mins: number) => { const x = ((mins % 1440) + 1440) % 1440; return `${String(Math.floor(x / 60)).padStart(2, "0")}:${String(x % 60).padStart(2, "0")}`; };
const modeLbl = (m?: string, online?: boolean) => (online != null ? (online ? "Online" : "In Person") : (String(m || "").includes("online") ? "Online" : "In Person"));

// ── Section heading used across the Available Options step ──
const SectionHead: React.FC<{ children: React.ReactNode; icon?: React.ReactNode; hint?: string }> = ({ children, icon, hint }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75, mt: 0.25 }}>
    {icon}
    <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em", color: "text.secondary" }}>{children}</Typography>
    {hint && <Typography sx={{ fontSize: 11, color: "text.disabled", fontWeight: 500 }}>· {hint}</Typography>}
  </Box>
);

/** A ✓ reason line (only ever fed backend-provided, true statements). */
const ReasonRow: React.FC<{ text: string; color?: string }> = ({ text, color = "#15803d" }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.6 }}>
    <CheckCircleRoundedIcon sx={{ fontSize: 14, color, mt: "1px", flexShrink: 0 }} />
    <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{text}</Typography>
  </Box>
);

/**
 * ONE staff recommendation card — used for the highlighted Best Match and the
 * compact "Other Available Staff" rows. It never shows a bare name: the matched
 * grade→subject mapping, availability window, mode/program and conflict status
 * are always rendered from the backend's structured `match_reasons`.
 */
const StaffCard: React.FC<{
  staff: any; studentName: string; dateLabel: string; start: string; end: string;
  best?: boolean; selected: boolean; onSelect: () => void; rankNote?: string;
}> = ({ staff, studentName, dateLabel, start, end, best, selected, onSelect, rankNote }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const teal = "#0d9488";
  const matched: any[] = Array.isArray(staff.matched_grade_subjects) ? staff.matched_grade_subjects : [];
  const broader: any[] = Array.isArray(staff.qualified_grade_subjects) ? staff.qualified_grade_subjects : [];
  const showBroader = gsCount(broader) > gsCount(matched);
  const reasons: string[] = Array.isArray(staff.match_reasons) ? staff.match_reasons : [];
  return (
    <Box onClick={onSelect} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}
      sx={{
        p: best ? 1.75 : 1.25, borderRadius: "12px", cursor: "pointer", position: "relative",
        border: `1px solid ${selected ? primary : best ? alpha(teal, 0.4) : "rgba(15,23,42,0.12)"}`,
        bgcolor: selected ? alpha(primary, 0.06) : best ? alpha(teal, 0.05) : "#fff",
        transition: "border-color .15s, background-color .15s",
        "&:hover": { borderColor: selected ? primary : best ? teal : "rgba(15,23,42,0.25)" },
      }}>
      {best && (
        <Chip icon={<AutoAwesomeIcon sx={{ fontSize: "13px !important" }} />} label="Best Match" size="small"
          sx={{ position: "absolute", top: -10, left: 14, height: 20, fontSize: 10, fontWeight: 800,
            bgcolor: teal, color: "#fff", "& .MuiChip-icon": { color: "#fff" } }} />
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: matched.length ? 1 : 0 }}>
        <Avatar sx={{ width: best ? 40 : 32, height: best ? 40 : 32, fontSize: best ? 14 : 12, fontWeight: 700, bgcolor: alpha(primary, 0.15), color: primary }}>{initials(staff.name)}</Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: best ? 15 : 13, fontWeight: 700 }}>{staff.name}</Typography>
            {staff.is_original && <Chip label="Original teacher" size="small" sx={{ height: 17, fontSize: 9.5, fontWeight: 700, bgcolor: alpha("#f59e0b", 0.14), color: "#b45309" }} />}
          </Box>
          <Typography sx={{ fontSize: 11.5, color: teal, fontWeight: 600 }}>Suitable for {studentName || "this student"}</Typography>
        </Box>
        {selected && <CheckCircleRoundedIcon sx={{ fontSize: 22, color: primary }} />}
      </Box>

      {!!matched.length && (
        <Box sx={{ mb: 0.75 }}>
          <GradeSubjectGroups groups={matched} dense />
        </Box>
      )}
      {showBroader && (
        <Typography sx={{ fontSize: 10.5, color: "text.disabled", mb: 0.5 }}>
          Also qualified for {broader.map((g) => `${typeof g.grade === "string" ? g.grade : g.grade?.name} ${(g.subjects || []).map((s: any) => (typeof s === "string" ? s : s?.name)).join(" & ")}`).join(", ")}
        </Typography>
      )}

      {best ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 0.4, mt: 0.5 }}>
          {reasons.map((r, i) => <ReasonRow key={i} text={r} />)}
          <ReasonRow text={`Available ${dateLabel} · ${fmtTime(start)}–${fmtTime(end)}`} />
          <ReasonRow text="No schedule conflicts" />
        </Box>
      ) : (
        <>
          <Typography sx={{ fontSize: 11, color: "success.main", fontWeight: 600 }}>
            Available {fmtTime(start)}–{fmtTime(end)} · {staff.program ? `${staff.program} · ` : ""}{modeLbl(staff.study_mode)}
          </Typography>
          {rankNote && <Typography sx={{ fontSize: 10.5, color: "text.disabled", mt: 0.25 }}>{rankNote}</Typography>}
        </>
      )}
    </Box>
  );
};

/** A JOIN-an-existing-class card with full details for a safe decision. */
const ExistingClassCard: React.FC<{ cls: any; studentName: string; selected: boolean; onSelect: () => void }> = ({ cls, studentName, selected, onSelect }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const online = !!cls.is_online;
  const seats = cls.seats_available;
  const reasons: string[] = Array.isArray(cls.match_reasons) ? cls.match_reasons : [];
  const Detail: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
    <Box><Typography sx={{ fontSize: 9.5, fontWeight: 700, color: "text.disabled", textTransform: "uppercase" }}>{label}</Typography>
      <Typography sx={{ fontSize: 11.5, fontWeight: 600 }}>{value ?? "—"}</Typography></Box>
  );
  return (
    <Box onClick={onSelect} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}
      sx={{ p: 1.25, borderRadius: "12px", cursor: "pointer", height: "100%",
        border: `1px solid ${selected ? primary : "rgba(15,23,42,0.12)"}`, bgcolor: selected ? alpha(primary, 0.06) : "#fff",
        "&:hover": { borderColor: selected ? primary : "rgba(15,23,42,0.25)" } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>{dayjs(cls.date).format("dddd, MMM D, YYYY")}</Typography>
          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{fmtTime(cls.start_time)}–{fmtTime(cls.end_time)}</Typography>
        </Box>
        {selected && <CheckCircleRoundedIcon sx={{ fontSize: 20, color: primary }} />}
      </Box>

      <Box sx={{ my: 0.75 }}><GradeSubjectGroups groups={cls.grade_subjects} dense /></Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.75, mb: 0.75 }}>
        <Detail label="Staff" value={cls.staff_name || "Unassigned"} />
        <Detail label="Program" value={cls.program || "—"} />
        <Detail label="Class Type" value={cls.class_type || "—"} />
        <Detail label="Study Mode" value={modeLbl(undefined, online)} />
        <Detail label="Location" value={online ? "Not applicable" : (cls.location || "—")} />
        <Detail label="Capacity" value={cls.capacity != null ? `${cls.enrolled ?? 0} enrolled · ${seats ?? 0} seat${seats === 1 ? "" : "s"} left` : `${cls.enrolled ?? 0} enrolled`} />
      </Box>

      {!!reasons.length && (
        <Box sx={{ p: 0.75, borderRadius: "8px", bgcolor: alpha("#22c55e", 0.06), border: `1px solid ${alpha("#22c55e", 0.18)}` }}>
          {reasons.map((r, i) => <ReasonRow key={i} text={r} />)}
        </Box>
      )}
    </Box>
  );
};

type Sel =
  | { kind: "new"; staffId: number; staffName: string; start: string; end: string }
  | { kind: "existing"; occurrenceId: number; label: string }
  | null;

/**
 * Student-specific "cannot attend" workflow — Remove Student (this occurrence
 * only) and optionally Schedule a linked Makeup with REAL, verified staff
 * availability. The assigned students, grade-subjects, mode, location and times
 * are read from the occurrence's own details endpoint (the source of truth) so
 * the dialog can never inherit a stale/global student from page filters.
 */
const MakeupWorkflow: React.FC<{
  open: boolean;
  row: any;                          // occurrence row (id, className, times… — used only for the id + a loading fallback)
  initialMode: "remove" | "makeup";
  preselectStudentId?: number | string | null;
  onClose: () => void;
  onDone?: () => void;
}> = ({ open, row, initialMode, preselectStudentId, onClose, onDone }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showMessage, showErrorMessage } = useNotifier();
  const confirm = useConfirm();
  const [removeStudent, { isLoading: removing }] = useRemoveStudentFromOccurrenceMutation();
  const [getOptions, { isLoading: loadingOptions }] = useGetMakeupOptionsMutation();
  const [scheduleMakeup, { isLoading: booking }] = useScheduleMakeupMutation();

  const occId = row?.id;
  // Source of truth: this occurrence's own students / grade-subjects / mode / location / times.
  const { data: detailsResp, isFetching: detailsLoading } = useGetScheduleDetailsQuery(occId ?? 0, { skip: !open || !occId });
  const details: any = detailsResp?.data;
  const occStudents: any[] = useMemo(() => (Array.isArray(details?.students) ? details.students : []), [details]);
  const cls = details?.class;
  const ses = details?.session;
  const isOnlineOriginal = !!cls?.is_online;

  const { data: locResp } = useGetallLocationQuery({ searchText: "" } as any, { skip: !open });
  const locations: any[] = useMemo(() => (Array.isArray(locResp?.data) ? locResp!.data as any[] : []), [locResp]);

  // ── State (all reset on open / occurrence change — never carried over) ──
  const [step, setStep] = useState(S_STUDENT);
  const [studentId, setStudentId] = useState("");
  const [reasonSel, setReasonSel] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [notes, setNotes] = useState("");
  // "now" = pick a replacement now (existing behavior), "later" = create a
  // pending record and remove the student without scheduling yet, "no" = no makeup.
  const [makeupAction, setMakeupAction] = useState<"now" | "later" | "no">(initialMode === "makeup" ? "now" : "no");
  const wantMakeup = makeupAction === "now"; // legacy alias used below

  const [makeupDate, setMakeupDate] = useState<Dayjs | null>(null);
  const [startTime, setStartTime] = useState("");  // "HH:mm"
  const [endTime, setEndTime] = useState("");      // "HH:mm"
  const [studyMode, setStudyMode] = useState<"online" | "in_person">("online");
  const [locationId, setLocationId] = useState("");
  const [subjectIds, setSubjectIds] = useState<number[]>([]);
  const [staffPref, setStaffPref] = useState<"any" | "original">("any");

  const [options, setOptions] = useState<any>(null);
  const [sel, setSel] = useState<Sel>(null);
  const [tried, setTried] = useState(false);

  const hydratedRef = useRef(false);
  const lastStudentRef = useRef<string>("");

  // Grade-subject catalogue for the currently selected student's grade.
  const gradeSubjectOptions = useMemo(() => {
    const gs: any[] = Array.isArray(details?.grade_subjects) ? details.grade_subjects : [];
    if (!gs.length) return [] as any[];
    const st = occStudents.find((s) => String(s.id) === String(studentId));
    let group: any = null;
    if (gs.length === 1) group = gs[0];
    else if (st?.grade) group = gs.find((g) => String(g?.grade?.name) === String(st.grade));
    const subs = group ? group.subjects : gs.flatMap((g) => g.subjects);
    // de-dupe by id
    const seen = new Set<number>();
    return (subs || []).filter((s: any) => (seen.has(s.id) ? false : (seen.add(s.id), true)));
  }, [details, occStudents, studentId]);

  const originalDate = useMemo(() => {
    const d = ses?.scheduled_date || row?.scheduledDate;
    return d ? dayjs(d).startOf("day") : null;
  }, [ses, row]);

  // 1) Hard reset every time the dialog opens or the occurrence changes — so no
  //    field can leak from a previous makeup flow while details load.
  useEffect(() => {
    if (!open) { hydratedRef.current = false; return; }
    hydratedRef.current = false;
    lastStudentRef.current = "";
    setStep(S_STUDENT); setStudentId("");
    setReasonSel(""); setCustomReason(""); setNotes("");
    setMakeupAction(initialMode === "makeup" ? "now" : "no");
    setMakeupDate(null); setStartTime(""); setEndTime("");
    setStudyMode("online"); setLocationId(""); setSubjectIds([]); setStaffPref("any");
    setOptions(null); setSel(null); setTried(false);
  }, [open, occId, initialMode]);

  // 2) Hydrate defaults ONCE, from the occurrence's real details.
  useEffect(() => {
    if (!open || detailsLoading || !details || hydratedRef.current) return;
    hydratedRef.current = true;

    // Student resolution — never infer from unrelated page state.
    const filteredAssigned = preselectStudentId != null && occStudents.some((s) => String(s.id) === String(preselectStudentId));
    let resolved = "";
    if (occStudents.length === 1) resolved = String(occStudents[0].id);
    else if (filteredAssigned) resolved = String(preselectStudentId);
    setStudentId(resolved);
    lastStudentRef.current = resolved;

    // Mode / location default = original occurrence.
    setStudyMode(isOnlineOriginal ? "online" : "in_person");

    // Times default = original session times (duration preserved).
    setStartTime(hhmm(ses?.start_time));
    setEndTime(hhmm(ses?.end_time));

    // Multi-student occurrences require an explicit pick unless resolved above.
    setStep(resolved ? S_ABSENCE : S_STUDENT);
  }, [open, detailsLoading, details, occStudents, preselectStudentId, isOnlineOriginal, ses]);

  // Default the in-person location to the original class's location (by name)
  // once both details and the locations list are available.
  useEffect(() => {
    if (!open || studyMode !== "in_person" || locationId) return;
    const name = String(cls?.location || "");
    if (!name) return;
    const match = locations.find((l) => String(l.location_name || "") === name);
    if (match) setLocationId(String(match.id));
  }, [open, studyMode, locationId, cls, locations]);

  // When the selected student changes, reset the makeup subjects to that
  // student's real grade-subjects (default = all) and drop stale availability.
  useEffect(() => {
    if (!open || !details || !studentId) return;
    if (lastStudentRef.current === studentId && subjectIds.length) return;
    lastStudentRef.current = studentId;
    setSubjectIds(gradeSubjectOptions.map((s: any) => s.id));
  }, [open, details, studentId, gradeSubjectOptions]);

  // Any change to the makeup parameters invalidates previously loaded availability.
  useEffect(() => {
    setOptions(null); setSel(null);
  }, [makeupDate, startTime, endTime, studyMode, locationId, studentId, JSON.stringify(subjectIds)]);

  const studentName = useMemo(
    () => occStudents.find((s) => String(s.id) === String(studentId))?.name || "",
    [occStudents, studentId]
  );
  const effectiveReason = reasonSel === "Other" ? customReason.trim() : reasonSel;
  const reasonTypePayload = reasonSel === "Other" ? "other" : reasonSel;
  const reasonValid = reasonSel !== "" && (reasonSel !== "Other" || customReason.trim().length > 0);

  const durationMin = startTime && endTime ? toMin(endTime) - toMin(startTime) : 0;
  const dateValid = !!makeupDate && (!originalDate || !makeupDate.startOf("day").isBefore(originalDate));
  const timeValid = !!startTime && !!endTime && durationMin > 0;
  const locationValid = studyMode === "online" || !!locationId;
  const prefsValid = dateValid && timeValid && subjectIds.length > 0 && locationValid;

  const originalStaffId = Number(options?.original_occurrence?.staff_id ?? details?.staff?.id ?? row?.staffId ?? 0);
  const keepOriginal = <T extends { staff_id?: number; id?: number; is_original?: boolean }>(arr: T[] = []) =>
    staffPref === "original"
      ? arr.filter((x) => Number(x.staff_id ?? x.id) === originalStaffId || x.is_original)
      : arr;

  // ── Ranked available staff (backend already sorted best-first + flagged best). ──
  const availStaff: any[] = useMemo(() => keepOriginal(options?.available_staff ?? []), [options, staffPref, originalStaffId]); // eslint-disable-line react-hooks/exhaustive-deps
  const bestStaff = availStaff[0] || null;
  const otherStaff = availStaff.slice(1);
  const existingClasses: any[] = useMemo(() => keepOriginal(options?.existing_classes ?? []), [options, staffPref, originalStaffId]); // eslint-disable-line react-hooks/exhaustive-deps
  const altTimes: any[] = useMemo(() => keepOriginal(options?.alternative_times ?? []), [options, staffPref, originalStaffId]); // eslint-disable-line react-hooks/exhaustive-deps
  const busyStaff: any[] = useMemo(() => keepOriginal(options?.unavailable_staff_suggestions ?? []), [options, staffPref, originalStaffId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Student requirement (their ACTUAL grade only) vs the full class mapping (§12).
  const student = occStudents.find((s) => String(s.id) === String(studentId));
  const requiredGradeSubjects: any[] = useMemo(() => {
    if (Array.isArray(options?.required_grade_subjects) && options.required_grade_subjects.length) return options.required_grade_subjects;
    // Fallback before options load: the selected subjects at the student's grade.
    const names = gradeSubjectOptions.filter((s: any) => subjectIds.includes(s.id)).map((s: any) => s.name);
    return student?.grade && names.length ? [{ grade: student.grade, subjects: names }] : [];
  }, [options, gradeSubjectOptions, subjectIds, student]);
  const requiredSubjectNames = gradeSubjectOptions.filter((s: any) => subjectIds.includes(s.id)).map((s: any) => s.name);
  const originalGradeSubjects: any[] = Array.isArray(details?.grade_subjects) ? details.grade_subjects : [];
  const dateLabel = makeupDate ? makeupDate.format("ddd, MMM D") : "";

  // Why a non-best available teacher ranks below the best — from real score/coverage.
  const rankNoteFor = (s: any): string | undefined => rankNote(bestStaff, s);

  // Compact selection summary shown above the footer (§15).
  const selSummary = useMemo(() => {
    if (!sel) return null;
    if (sel.kind === "existing") return { title: `Join ${sel.label.split(" · ")[2] || "an existing class"}`, detail: sel.label };
    return { title: `One-off makeup with ${sel.staffName}`, detail: `${dateLabel} · ${fmtTime(sel.start)}–${fmtTime(sel.end)}` };
  }, [sel, dateLabel]);

  // ── Actions ──
  /**
   * Turn Out WITHOUT a makeup. Confirms first, because there is no follow-up step
   * to back out of — unlike the makeup branch, this commits immediately.
   *
   * `removing` (the mutation's own in-flight flag) guards re-entry, so a double
   * click or a double Enter can't fire two removals for the same student.
   */
  const doRemoveOnly = async () => {
    if (removing) return;
    const isLater = makeupAction === "later";
    const ok = await confirm({
      title: isLater ? "Mark makeup pending?" : "Confirm absence?",
      message: isLater
        ? `Remove ${studentName || "this student"} from this occurrence and add a pending makeup? You can schedule the replacement later.`
        : `Remove ${studentName || "this student"} from this occurrence without scheduling a makeup? The class stays scheduled for everyone else.`,
      confirmText: isLater ? "Confirm & Mark Makeup Pending" : "Confirm Absence",
      variant: "warning",
    });
    if (!ok) return;
    try {
      const res = await removeStudent({
        occurrenceId: occId, student_id: Number(studentId),
        reason_type: reasonTypePayload, reason: effectiveReason, notes,
        makeup_required: isLater, schedule_later: isLater,
      }).unwrap();
      if (res.status) { showMessage(res.message); onDone?.(); onClose(); }
      else showErrorMessage(res.message || "Failed to update student");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed to update student"); }
  };

  const fetchOptions = async () => {
    setSel(null); setOptions(null);
    try {
      const res = await getOptions({
        occurrenceId: occId, student_id: Number(studentId),
        date: makeupDate!.format("YYYY-MM-DD"), start_time: hhmm(startTime), end_time: hhmm(endTime),
        study_mode: studyMode, location_id: studyMode === "in_person" ? Number(locationId) || undefined : undefined,
        subject_ids: subjectIds,
      }).unwrap();
      if (res.status) { setOptions(res.data); setStep(S_AVAIL); }
      else showErrorMessage(res.message || "Failed to load options");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed to load options"); }
  };

  const confirmMakeup = async () => {
    if (!sel) return;
    const base: any = {
      occurrenceId: occId, student_id: Number(studentId),
      reason_type: reasonTypePayload, reason: effectiveReason, notes, subject_ids: subjectIds,
    };
    if (sel.kind === "existing") base.existing_occurrence_id = sel.occurrenceId;
    else {
      base.makeup_date = makeupDate!.format("YYYY-MM-DD");
      base.start_time = sel.start; base.end_time = sel.end; base.staff_id = sel.staffId;
      base.study_mode = studyMode;
      base.location_id = studyMode === "in_person" ? Number(locationId) || undefined : undefined;
    }
    try {
      const res = await scheduleMakeup(base).unwrap();
      if (res.status) { showMessage(res.message); onDone?.(); onClose(); }
      else { showErrorMessage(res.message || "Failed to schedule makeup"); setStep(S_AVAIL); setSel(null); } // save-time conflict → back to options
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed to schedule makeup"); setStep(S_AVAIL); setSel(null); }
  };

  // ── Small presentational atoms ──
  const Ctx: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
    <Box>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>{label}</Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{value ?? "—"}</Typography>
    </Box>
  );

  const wide = step >= S_AVAIL;
  // "Cannot Attend" is the user-facing wording for the per-student removal —
  // clearer than the internal "turn out" jargon. The underlying operation is
  // unchanged (removeStudentFromOccurrence): it never cancels the occurrence and
  // never touches another student.
  const title =
    initialMode === "remove"
      ? (studentName ? `${studentName} Cannot Attend` : "Student Cannot Attend")
      : "Schedule Makeup Class";
  const modeLabel = isOnlineOriginal ? "Online" : "In Person";
  const locLabel = isOnlineOriginal ? "Not applicable" : (cls?.location || row?.locationName || "—");

  return (
    <Dialog open={open} onClose={onClose} maxWidth={wide ? "md" : "sm"} fullWidth fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : "14px" } }}>
      <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
        {initialMode === "remove" ? <PersonRemoveOutlinedIcon fontSize="small" /> : <EventRepeatOutlinedIcon fontSize="small" />}
        {/* The "remove" title already carries the student's name, so only the
            makeup title needs the "— Name" suffix. */}
        {title}{studentName && initialMode !== "remove" ? ` — ${studentName}` : ""}
      </DialogTitle>

      <DialogContent>
        {detailsLoading && !details ? (
          <Box sx={{ textAlign: "center", py: 5 }}><CircularProgress size={24} /></Box>
        ) : (
          <>
            {/* Compact original-class context for the early steps. The Available
                Options step renders a richer grade-wise summary instead (below). */}
            {step < S_AVAIL && (
              <Box sx={{ p: 1.5, mb: 2, borderRadius: "10px", bgcolor: alpha("#0f172a", 0.02), border: "1px solid rgba(15,23,42,0.08)", display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" }, gap: 1.25 }}>
                <Ctx label="Class" value={cls?.display_name || row?.className || "Multi-grade scheduled class"} />
                <Ctx label="Original date" value={originalDate ? originalDate.format("MMM D, YYYY") : "—"} />
                <Ctx label="Original time" value={`${fmtTime(ses?.start_time || row?.startTime)}–${fmtTime(ses?.end_time || row?.endTime)}`} />
                <Ctx label="Original staff" value={details?.staff?.name || row?.staffName || "Unassigned"} />
                <Ctx label="Mode" value={modeLabel} />
                <Ctx label="Location" value={locLabel} />
              </Box>
            )}

            {/* Step 0 — pick the student (multi-student occurrences) */}
            {step === S_STUDENT && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                  This session has more than one student. Choose who this applies to.
                </Typography>
                <TextField select label="Which student? *" size="small" fullWidth value={studentId}
                  onChange={(e) => setStudentId(e.target.value)} error={tried && !studentId}>
                  <MenuItem value="">Select a student…</MenuItem>
                  {occStudents.map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>{s.name}{s.grade ? ` · ${s.grade}` : ""}</MenuItem>
                  ))}
                </TextField>
                {tried && !studentId && <FormHelperText error>Please select a student.</FormHelperText>}
              </Box>
            )}

            {/* Step 1 — absence details */}
            {step === S_ABSENCE && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ p: 1.25, borderRadius: "8px", bgcolor: alpha(theme.palette.warning.main, 0.08), border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}` }}>
                  <Typography sx={{ fontSize: 11.5, color: "#b45309", fontWeight: 600 }}>
                    This removes only {studentName || "this student"} from this occurrence. The class remains scheduled for all other students.
                  </Typography>
                </Box>
                <TextField select label="Reason *" size="small" fullWidth value={reasonSel}
                  onChange={(e) => { setReasonSel(e.target.value); if (e.target.value !== "Other") setCustomReason(""); }}
                  error={tried && reasonSel === ""}
                  helperText={tried && reasonSel === "" ? "Please select a reason." : " "}>
                  <MenuItem value="">Select a reason…</MenuItem>
                  {REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </TextField>
                {reasonSel === "Other" && (
                  <TextField label="Specify reason *" size="small" fullWidth value={customReason}
                    inputProps={{ maxLength: REASON_MAX }}
                    placeholder="Enter the reason for missing this class"
                    onChange={(e) => setCustomReason(e.target.value)}
                    error={tried && customReason.trim().length === 0}
                    helperText={tried && customReason.trim().length === 0 ? "Please enter the reason." : `${customReason.length}/${REASON_MAX}`} />
                )}
                <TextField label="Notes (optional)" size="small" fullWidth multiline rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
                {/* An explicit choice, not an easy-to-miss checkbox: whether the
                    student gets a replacement class is a real decision and the two
                    branches behave very differently. */}
                {initialMode === "remove" && (
                  <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 0.25 }}>Makeup Action *</Typography>
                    <RadioGroup value={makeupAction} onChange={(e) => setMakeupAction(e.target.value as any)}>
                      <FormControlLabel value="now" control={<Radio size="small" />}
                        label={<Box><Typography sx={{ fontSize: 13, fontWeight: 600 }}>Schedule makeup now</Typography>
                          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>Find or create a replacement class immediately.</Typography></Box>} />
                      <FormControlLabel value="later" control={<Radio size="small" />}
                        label={<Box><Typography sx={{ fontSize: 13, fontWeight: 600 }}>Schedule makeup later</Typography>
                          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>Mark this absence as requiring a makeup. The date can be selected later.</Typography></Box>} />
                      <FormControlLabel value="no" control={<Radio size="small" />}
                        label={<Box><Typography sx={{ fontSize: 13, fontWeight: 600 }}>No makeup required</Typography>
                          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>Remove the student from this occurrence without a replacement.</Typography></Box>} />
                    </RadioGroup>
                    <FormHelperText sx={{ mt: 0.5 }}>
                      {makeupAction === "now" && "You'll pick the date, time and a qualified, available teacher next."}
                      {makeupAction === "later" && `${studentName || "This student"} will be removed from this occurrence and added to the Pending Makeups list. The replacement class can be scheduled later.`}
                      {makeupAction === "no" && `${studentName || "This student"} will be removed from this occurrence and no pending makeup will be created.`}
                    </FormHelperText>
                  </Box>
                )}
              </Box>
            )}

            {/* Step 2 — makeup preferences */}
            {step === S_PREFS && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
                  <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                    Set the makeup date, time, mode and subjects. We only search for teachers who can teach {studentName || "this student"}'s grade &amp; the selected subjects and are genuinely free.
                  </Typography>

                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <Box sx={{ flex: "1 1 200px" }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.5 }}>Makeup date *</Typography>
                      <DatePicker value={makeupDate} onChange={(v) => setMakeupDate(v)} minDate={originalDate || undefined}
                        slotProps={{ textField: { size: "small", fullWidth: true, error: tried && !dateValid,
                          helperText: tried && !dateValid ? (makeupDate ? "Must be on or after the original class date." : "Makeup date is required.") : " " } }} />
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                    <Box sx={{ flex: 1 }}>
                      <PremiumTimePicker label="Start time *" value={startTime}
                        error={tried && (!startTime || durationMin <= 0)}
                        onChange={(v) => {
                          // Preserve the original duration when the start shifts.
                          const dur = startTime && endTime ? toMin(endTime) - toMin(startTime) : 0;
                          setStartTime(v);
                          if (v && dur > 0) setEndTime(minToHHmm(toMin(v) + dur));
                        }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <PremiumTimePicker label="End time *" value={endTime}
                        error={tried && (!endTime || durationMin <= 0)}
                        helperText={endTime && startTime && durationMin <= 0 ? "End time must be later than start time." : (durationMin > 0 ? `${durationMin} min` : undefined)}
                        onChange={(v) => setEndTime(v)} />
                    </Box>
                  </Box>

                  <TextField select label="Study mode *" size="small" fullWidth value={studyMode}
                    onChange={(e) => { setStudyMode(e.target.value as any); if (e.target.value === "online") setLocationId(""); }}>
                    <MenuItem value="online">Online</MenuItem>
                    <MenuItem value="in_person">In Person</MenuItem>
                  </TextField>

                  {studyMode === "in_person" ? (
                    <TextField select label="Location *" size="small" fullWidth value={locationId}
                      onChange={(e) => setLocationId(e.target.value)} error={tried && !locationId}
                      helperText={tried && !locationId ? "Location is required for in-person classes." : " "}>
                      <MenuItem value="">Select a location…</MenuItem>
                      {locations.map((l) => <MenuItem key={l.id} value={String(l.id)}>{l.location_name || l.location_address}</MenuItem>)}
                    </TextField>
                  ) : (
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Location: <b>Not applicable</b> (online).</Typography>
                  )}

                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.5 }}>Subjects to make up *</Typography>
                    {gradeSubjectOptions.length ? (
                      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                        {gradeSubjectOptions.map((su: any) => {
                          const on = subjectIds.includes(su.id);
                          return (
                            <Chip key={su.id} label={su.name} size="small"
                              onClick={() => setSubjectIds((p) => (on ? p.filter((x) => x !== su.id) : [...p, su.id]))}
                              sx={{ height: 28, fontSize: 12, cursor: "pointer", fontWeight: on ? 700 : 500,
                                bgcolor: on ? alpha(theme.palette.primary.main, 0.14) : alpha("#0f172a", 0.05),
                                color: on ? theme.palette.primary.main : "text.primary",
                                border: `1px solid ${on ? theme.palette.primary.main : "transparent"}` }} />
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>No grade-specific subjects found for this student.</Typography>
                    )}
                    {tried && subjectIds.length === 0 && <FormHelperText error>Select at least one subject.</FormHelperText>}
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.25 }}>Staff preference</Typography>
                    <RadioGroup row value={staffPref} onChange={(e) => setStaffPref(e.target.value as any)}>
                      <FormControlLabel value="any" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13 }}>Any suitable staff</Typography>} />
                      <FormControlLabel value="original" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13 }}>Original teacher only</Typography>} />
                    </RadioGroup>
                  </Box>
                </Box>
              </LocalizationProvider>
            )}

            {/* Step 3 — real availability options (ranked, explained) */}
            {step === S_AVAIL && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
                {loadingOptions ? (
                  <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={22} /></Box>
                ) : (
                  <>
                    {/* 1 — Student Requirement vs Original Class (kept separate, §12) */}
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.25 }}>
                      <Box sx={{ p: 1.25, borderRadius: "12px", border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                        <SectionHead icon={<GroupsOutlinedIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />}>Student Requirement</SectionHead>
                        <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{studentName}{student?.grade ? <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 600, color: "text.secondary" }}> · {student.grade}</Typography> : null}</Typography>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", mt: 0.75, mb: 0.4 }}>Required subjects</Typography>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {requiredSubjectNames.length ? requiredSubjectNames.map((n: string) => (
                            <Chip key={n} label={n} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: "#fff", border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`, color: theme.palette.primary.main }} />
                          )) : <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>—</Typography>}
                        </Box>
                      </Box>
                      <Box sx={{ p: 1.25, borderRadius: "12px", border: "1px solid rgba(15,23,42,0.1)", bgcolor: alpha("#0f172a", 0.015) }}>
                        <SectionHead>Original Class Supports</SectionHead>
                        <GradeSubjectGroups groups={originalGradeSubjects} dense />
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.6, mt: 0.9 }}>
                          <Ctx label="Original date" value={originalDate ? originalDate.format("ddd, MMM D, YYYY") : "—"} />
                          <Ctx label="Original time" value={`${fmtTime(ses?.start_time || row?.startTime)}–${fmtTime(ses?.end_time || row?.endTime)}`} />
                          <Ctx label="Original staff" value={details?.staff?.name || row?.staffName || "Unassigned"} />
                          <Ctx label="Mode" value={modeLabel} />
                          <Ctx label="Location" value={locLabel} />
                        </Box>
                      </Box>
                    </Box>

                    {options?.student_conflict && (
                      <Box sx={{ p: 1, borderRadius: "8px", bgcolor: alpha(theme.palette.error.main, 0.06), border: `1px solid ${alpha(theme.palette.error.main, 0.2)}` }}>
                        <Typography sx={{ fontSize: 12, color: "error.main", fontWeight: 600 }}>{studentName} already has a class ({options.student_conflict.label}) at this time. Choose another time.</Typography>
                      </Box>
                    )}

                    {/* 2 — Best Recommended Option */}
                    {bestStaff && (
                      <Box>
                        <SectionHead icon={<AutoAwesomeIcon sx={{ fontSize: 15, color: "#0d9488" }} />}>Best Recommended Option</SectionHead>
                        <StaffCard staff={bestStaff} studentName={studentName} dateLabel={dateLabel} start={hhmm(startTime)} end={hhmm(endTime)} best
                          selected={sel?.kind === "new" && sel.staffId === bestStaff.id && sel.start === hhmm(startTime)}
                          onSelect={() => setSel({ kind: "new", staffId: bestStaff.id, staffName: bestStaff.name, start: hhmm(startTime), end: hhmm(endTime) })} />
                      </Box>
                    )}

                    {/* 3 — Other Available Staff at the selected time */}
                    {!!otherStaff.length && (
                      <Box>
                        <SectionHead icon={<EventAvailableOutlinedIcon sx={{ fontSize: 15, color: "success.main" }} />} hint={`available ${fmtTime(hhmm(startTime))}–${fmtTime(hhmm(endTime))}`}>Other Available Staff</SectionHead>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                          {otherStaff.map((st: any) => (
                            <StaffCard key={st.id} staff={st} studentName={studentName} dateLabel={dateLabel} start={hhmm(startTime)} end={hhmm(endTime)}
                              rankNote={rankNoteFor(st)}
                              selected={sel?.kind === "new" && sel.staffId === st.id && sel.start === hhmm(startTime)}
                              onSelect={() => setSel({ kind: "new", staffId: st.id, staffName: st.name, start: hhmm(startTime), end: hhmm(endTime) })} />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {!bestStaff && (
                      <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                        {staffPref === "original" ? "The original teacher is not available at the selected time." : "No suitable staff are available at the exact selected time. See existing classes and alternative times below."}
                      </Typography>
                    )}

                    {/* 4 — Join an Existing Class (full details, 2-col on desktop) */}
                    {!!existingClasses.length && (
                      <Box>
                        <SectionHead hint="student not yet enrolled · seats available">Join an Existing Class</SectionHead>
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
                          {existingClasses.map((c: any) => (
                            <ExistingClassCard key={c.occurrence_id} cls={c} studentName={studentName}
                              selected={sel?.kind === "existing" && sel.occurrenceId === c.occurrence_id}
                              onSelect={() => setSel({ kind: "existing", occurrenceId: c.occurrence_id, label: `${dayjs(c.date).format("ddd, MMM D")} · ${fmtTime(c.start_time)}–${fmtTime(c.end_time)} · ${c.staff_name}` })} />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* 5 — Suggested Alternative Times (structured, closest first) */}
                    {!!altTimes.length && (
                      <Box>
                        <SectionHead hint="closest to your requested time">Suggested Alternative Times</SectionHead>
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 0.75 }}>
                          {altTimes.map((a: any, i: number) => {
                            const on = sel?.kind === "new" && sel.staffId === a.staff_id && sel.start === a.start_time;
                            return (
                              <Box key={i} onClick={() => setSel({ kind: "new", staffId: a.staff_id, staffName: a.staff_name, start: a.start_time, end: a.end_time })}
                                role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSel({ kind: "new", staffId: a.staff_id, staffName: a.staff_name, start: a.start_time, end: a.end_time }); } }}
                                sx={{ p: 1, borderRadius: "10px", cursor: "pointer", border: `1px solid ${on ? theme.palette.primary.main : "rgba(15,23,42,0.12)"}`, bgcolor: on ? alpha(theme.palette.primary.main, 0.06) : "#fff", display: "flex", alignItems: "center", gap: 1 }}>
                                <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main }}>{initials(a.staff_name)}</Avatar>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{fmtTime(a.start_time)}–{fmtTime(a.end_time)}</Typography>
                                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{a.staff_name} · qualified · {studyMode === "online" ? "Online" : "In Person"}</Typography>
                                </Box>
                                {on && <CheckCircleRoundedIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}

                    {/* 6 — Suitable Staff Unavailable at Selected Time */}
                    {!!busyStaff.length && (
                      <Box>
                        <SectionHead icon={<EventBusyOutlinedIcon sx={{ fontSize: 15, color: "text.disabled" }} />}>Suitable Staff — Unavailable at Selected Time</SectionHead>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                          {busyStaff.map((st: any) => (
                            <Box key={st.id} sx={{ p: 1, borderRadius: "10px", border: "1px dashed rgba(15,23,42,0.18)", bgcolor: alpha("#0f172a", 0.015) }}>
                              <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{st.name}<Typography component="span" sx={{ fontSize: 11, color: "text.secondary", fontWeight: 500 }}> — busy {st.conflict}</Typography></Typography>
                              {!!(st.matched_grade_subjects?.length) && <Box sx={{ mt: 0.5 }}><GradeSubjectGroups groups={st.matched_grade_subjects} dense /></Box>}
                              {!!st.next_free?.length && (
                                <>
                                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", mt: 0.6, mb: 0.3 }}>Next available</Typography>
                                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                                    {st.next_free.map((nf: any, i: number) => {
                                      const on = sel?.kind === "new" && sel.staffId === st.id && sel.start === nf.start_time;
                                      return (
                                        <Chip key={i} size="small" label={`${fmtTime(nf.start_time)}–${fmtTime(nf.end_time)}`}
                                          onClick={() => setSel({ kind: "new", staffId: st.id, staffName: st.name, start: nf.start_time, end: nf.end_time })}
                                          sx={{ height: 24, fontSize: 11, cursor: "pointer", bgcolor: on ? alpha(theme.palette.primary.main, 0.14) : alpha("#0f172a", 0.05), color: on ? theme.palette.primary.main : "text.primary" }} />
                                      );
                                    })}
                                  </Box>
                                </>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {!bestStaff && !altTimes.length && !existingClasses.length && !busyStaff.length && (
                      <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>No suitable staff have availability on this date. Try another date, time or mode.</Typography>
                    )}
                  </>
                )}
              </Box>
            )}

            {/* Step 4 — review */}
            {step === S_REVIEW && sel && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, textTransform: "uppercase", color: "text.secondary" }}>Review</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: "10px", border: "1px solid rgba(15,23,42,0.1)" }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: "text.secondary", mb: 1 }}>ORIGINAL (MISSED)</Typography>
                    <Stack spacing={1}>
                      <Ctx label="Student" value={studentName} />
                      <Ctx label="Date" value={originalDate ? originalDate.format("ddd, MMM D, YYYY") : "—"} />
                      <Ctx label="Time" value={`${fmtTime(ses?.start_time)}–${fmtTime(ses?.end_time)}`} />
                      <Ctx label="Mode" value={modeLabel} />
                      <Ctx label="Reason" value={effectiveReason} />
                    </Stack>
                  </Box>
                  <Box sx={{ p: 1.5, borderRadius: "10px", border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: theme.palette.primary.main, mb: 1 }}>MAKEUP</Typography>
                    <Stack spacing={1}>
                      {sel.kind === "existing" ? (
                        <Ctx label="Join class" value={sel.label} />
                      ) : (
                        <>
                          <Ctx label="Date" value={dayjs(makeupDate!).format("ddd, MMM D, YYYY")} />
                          <Ctx label="Time" value={`${fmtTime(sel.start)}–${fmtTime(sel.end)}`} />
                          <Ctx label="Staff" value={sel.staffName} />
                          <Ctx label="Mode" value={studyMode === "online" ? "Online" : "In Person"} />
                          <Ctx label="Location" value={studyMode === "online" ? "Not applicable" : (locations.find((l) => String(l.id) === locationId)?.location_name || "—")} />
                        </>
                      )}
                      <Ctx label="Subjects" value={gradeSubjectOptions.filter((s: any) => subjectIds.includes(s.id)).map((s: any) => s.name).join(", ") || "—"} />
                    </Stack>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{studentName} will be removed from the original session (it is not cancelled for others) and this makeup linked to it. Availability is re-verified when you confirm.</Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* Compact selected-option summary above the footer (§15) — visible while
          choosing/reviewing so the pick is preserved and clear when scrolling. */}
      {(step === S_AVAIL || step === S_REVIEW) && selSummary && (
        <Box sx={{ px: 3, py: 1, borderTop: "1px solid rgba(15,23,42,0.08)", bgcolor: alpha(theme.palette.primary.main, 0.05), display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleRoundedIcon sx={{ fontSize: 18, color: theme.palette.primary.main, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 800, color: theme.palette.primary.main, textTransform: "uppercase", letterSpacing: "0.04em" }}>Selected</Typography>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selSummary.title}</Typography>
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{selSummary.detail}</Typography>
          </Box>
        </Box>
      )}

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: "text.secondary" }}>Cancel</Button>

        {step === S_STUDENT && (
          <Button variant="contained" onClick={() => { setTried(true); if (studentId) { setTried(false); setStep(S_ABSENCE); } }}>Continue</Button>
        )}

        {step === S_ABSENCE && (
          <>
            {occStudents.length > 1 && <Button onClick={() => { setTried(false); setStep(S_STUDENT); }} sx={{ color: "text.secondary" }}>Back</Button>}
            {initialMode === "remove" && makeupAction !== "now" ? (
              <Button variant="contained" color={makeupAction === "later" ? "warning" : "error"} disabled={removing}
                onClick={() => { setTried(true); if (reasonValid) doRemoveOnly(); }}
                startIcon={removing ? <CircularProgress size={15} color="inherit" /> : <PersonRemoveOutlinedIcon />}>
                {makeupAction === "later" ? "Confirm & Mark Makeup Pending" : "Confirm Absence"}
              </Button>
            ) : (
              <Button variant="contained" onClick={() => { setTried(true); if (reasonValid) { setTried(false); setStep(S_PREFS); } }}>Continue to makeup</Button>
            )}
          </>
        )}

        {step === S_PREFS && (
          <>
            <Button onClick={() => { setTried(false); setStep(S_ABSENCE); }} sx={{ color: "text.secondary" }}>Back</Button>
            <Button variant="contained" disabled={loadingOptions}
              onClick={() => { setTried(true); if (prefsValid) { setTried(false); fetchOptions(); } }}
              startIcon={loadingOptions ? <CircularProgress size={15} color="inherit" /> : undefined}>Find available staff</Button>
          </>
        )}

        {step === S_AVAIL && (
          <>
            <Button onClick={() => setStep(S_PREFS)} sx={{ color: "text.secondary" }}>Back</Button>
            <Button variant="contained" disabled={!sel} onClick={() => setStep(S_REVIEW)}>Review</Button>
          </>
        )}

        {step === S_REVIEW && (
          <>
            <Button onClick={() => setStep(S_AVAIL)} sx={{ color: "text.secondary" }}>Back</Button>
            <Button variant="contained" disabled={booking || !sel} onClick={confirmMakeup}
              startIcon={booking ? <CircularProgress size={15} color="inherit" /> : <EventRepeatOutlinedIcon />}>Confirm Makeup</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MakeupWorkflow;
